import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import MuiTable from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import { Translate, TextFormat, getPaginationState, JhiPagination, JhiItemCount } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT, APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getTourCategories } from 'app/entities/tour-category/tour-category.reducer';
import { getEntities as getPlaces } from 'app/entities/place/place.reducer';
import { getEntities as getPlaceCategories } from 'app/entities/place-category/place-category.reducer';
import { getEntities as getTourExtraCategories } from 'app/entities/tour-extra-category/tour-extra-category.reducer';
import { getEntities as getTourExtras } from 'app/entities/tour-extra/tour-extra.reducer';
import { getEntities as getMenus } from 'app/entities/menu/menu.reducer';
import { getEntities as getWebPages } from 'app/entities/web-page/web-page.reducer';
import { getEntities as getTags } from 'app/entities/tag/tag.reducer';
import { getEntities } from './content.reducer';

export const Content = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );
  const [selectedTourCategory, setSelectedTourCategory] = useState<string>('');
  const [selectedPlace, setSelectedPlace] = useState<string>('');
  const [selectedPlaceCategory, setSelectedPlaceCategory] = useState<string>('');
  const [selectedTourExtraCategory, setSelectedTourExtraCategory] = useState<string>('');
  const [selectedTourExtra, setSelectedTourExtra] = useState<string>('');
  const [selectedMenu, setSelectedMenu] = useState<string>('');
  const [selectedWebPage, setSelectedWebPage] = useState<string>('');
  const [selectedTag, setSelectedTag] = useState<string>('');

  const contentList = useAppSelector(state => state.content.entities);
  const loading = useAppSelector(state => state.content.loading);
  const totalItems = useAppSelector(state => state.content.totalItems);

  const tourCategories = useAppSelector(state => state.tourCategory.entities);
  const places = useAppSelector(state => state.place.entities);
  const placeCategories = useAppSelector(state => state.placeCategory.entities);
  const tourExtraCategories = useAppSelector(state => state.tourExtraCategory.entities);
  const tourExtras = useAppSelector(state => state.tourExtra.entities);
  const menus = useAppSelector(state => state.menu.entities);
  const webPages = useAppSelector(state => state.webPage.entities);
  const tags = useAppSelector(state => state.tag.entities);

  useEffect(() => {
    dispatch(getTourCategories({ size: 1000 }));
    dispatch(getPlaces({ size: 1000 }));
    dispatch(getPlaceCategories({ size: 1000 }));
    dispatch(getTourExtraCategories({ size: 1000 }));
    dispatch(getTourExtras({ size: 1000 }));
    dispatch(getMenus({ size: 1000 }));
    dispatch(getWebPages({ size: 1000 }));
    dispatch(getTags({ size: 1000 }));
  }, []);

  const getAllEntities = () => {
    const params: any = {
      page: paginationState.activePage - 1,
      size: paginationState.itemsPerPage,
      sort: `${paginationState.sort},${paginationState.order}`,
    };
    if (selectedTourCategory) params.tourCategoryId = Number(selectedTourCategory);
    if (selectedPlace) params.placeId = Number(selectedPlace);
    if (selectedPlaceCategory) params.placeCategoryId = Number(selectedPlaceCategory);
    if (selectedTourExtraCategory) params.tourExtraCategoryId = Number(selectedTourExtraCategory);
    if (selectedTourExtra) params.tourExtraId = Number(selectedTourExtra);
    if (selectedMenu) params.menuId = Number(selectedMenu);
    if (selectedWebPage) params.webPageId = Number(selectedWebPage);
    if (selectedTag) params.tagId = Number(selectedTag);
    dispatch(getEntities(params));
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [
    paginationState.activePage,
    paginationState.order,
    paginationState.sort,
    selectedTourCategory,
    selectedPlace,
    selectedPlaceCategory,
    selectedTourExtraCategory,
    selectedTourExtra,
    selectedMenu,
    selectedWebPage,
    selectedTag,
  ]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleTourCategoryFilterChange = (event: any) => {
    setSelectedTourCategory(event.target.value);
    setPaginationState({ ...paginationState, activePage: 1 });
  };

  const handlePlaceFilterChange = (event: any) => {
    setSelectedPlace(event.target.value);
    setPaginationState({ ...paginationState, activePage: 1 });
  };

  const handlePlaceCategoryFilterChange = (event: any) => {
    setSelectedPlaceCategory(event.target.value);
    setPaginationState({ ...paginationState, activePage: 1 });
  };

  const handleTourExtraCategoryFilterChange = (event: any) => {
    setSelectedTourExtraCategory(event.target.value);
    setPaginationState({ ...paginationState, activePage: 1 });
  };

  const handleTourExtraFilterChange = (event: any) => {
    setSelectedTourExtra(event.target.value);
    setPaginationState({ ...paginationState, activePage: 1 });
  };

  const handleMenuFilterChange = (event: any) => {
    setSelectedMenu(event.target.value);
    setPaginationState({ ...paginationState, activePage: 1 });
  };

  const handleWebPageFilterChange = (event: any) => {
    setSelectedWebPage(event.target.value);
    setPaginationState({ ...paginationState, activePage: 1 });
  };

  const handleTagFilterChange = (event: any) => {
    setSelectedTag(event.target.value);
    setPaginationState({ ...paginationState, activePage: 1 });
  };

  const handleClearAllFilters = () => {
    setSelectedTourCategory('');
    setSelectedPlace('');
    setSelectedPlaceCategory('');
    setSelectedTourExtraCategory('');
    setSelectedTourExtra('');
    setSelectedMenu('');
    setSelectedWebPage('');
    setSelectedTag('');
    setPaginationState({ ...paginationState, activePage: 1 });
  };

  const hasActiveFilters =
    selectedTourCategory ||
    selectedPlace ||
    selectedPlaceCategory ||
    selectedTourExtraCategory ||
    selectedTourExtra ||
    selectedMenu ||
    selectedWebPage ||
    selectedTag;

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    } else {
      return order === ASC ? faSortUp : faSortDown;
    }
  };

  return (
    <div>
      <h2 id="content-heading" data-cy="ContentHeading">
        <Translate contentKey="jhipsterSampleApplicationApp.content.home.title">Contents</Translate>
        <div className="d-flex justify-content-end">
          <Button
            className="me-2"
            onClick={handleSyncList}
            disabled={loading}
            variant="outlined"
            startIcon={<FontAwesomeIcon icon="sync" spin={loading} />}
          >
            <Translate contentKey="jhipsterSampleApplicationApp.content.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Button
            component={Link}
            to="/content/new"
            variant="contained"
            color="primary"
            id="jh-create-entity"
            data-cy="entityCreateButton"
            startIcon={<FontAwesomeIcon icon="plus" />}
          >
            <Translate contentKey="jhipsterSampleApplicationApp.content.home.createLabel">Create new Content</Translate>
          </Button>
        </div>
      </h2>
      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="content-filter-tour-category-label">
            <Translate contentKey="jhipsterSampleApplicationApp.content.tourCategory">Tour Category</Translate>
          </InputLabel>
          <Select
            labelId="content-filter-tour-category-label"
            id="content-filter-tour-category"
            value={selectedTourCategory}
            label={<Translate contentKey="jhipsterSampleApplicationApp.content.tourCategory">Tour Category</Translate>}
            onChange={handleTourCategoryFilterChange}
          >
            <MenuItem value="">
              <em>All Tour Categories</em>
            </MenuItem>
            {tourCategories
              ? tourCategories.map(tc => (
                  <MenuItem value={tc.id} key={tc.id}>
                    {tc.code}
                  </MenuItem>
                ))
              : null}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="content-filter-place-label">
            <Translate contentKey="jhipsterSampleApplicationApp.content.place">Place</Translate>
          </InputLabel>
          <Select
            labelId="content-filter-place-label"
            id="content-filter-place"
            value={selectedPlace}
            label={<Translate contentKey="jhipsterSampleApplicationApp.content.place">Place</Translate>}
            onChange={handlePlaceFilterChange}
          >
            <MenuItem value="">
              <em>All Places</em>
            </MenuItem>
            {places
              ? places.map(p => (
                  <MenuItem value={p.id} key={p.id}>
                    {p.code}
                  </MenuItem>
                ))
              : null}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="content-filter-place-category-label">
            <Translate contentKey="jhipsterSampleApplicationApp.content.placeCategory">Place Category</Translate>
          </InputLabel>
          <Select
            labelId="content-filter-place-category-label"
            id="content-filter-place-category"
            value={selectedPlaceCategory}
            label={<Translate contentKey="jhipsterSampleApplicationApp.content.placeCategory">Place Category</Translate>}
            onChange={handlePlaceCategoryFilterChange}
          >
            <MenuItem value="">
              <em>All Place Categories</em>
            </MenuItem>
            {placeCategories
              ? placeCategories.map(pc => (
                  <MenuItem value={pc.id} key={pc.id}>
                    {pc.code}
                  </MenuItem>
                ))
              : null}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="content-filter-tour-extra-category-label">
            <Translate contentKey="jhipsterSampleApplicationApp.content.tourExtraCategory">Tour Extra Category</Translate>
          </InputLabel>
          <Select
            labelId="content-filter-tour-extra-category-label"
            id="content-filter-tour-extra-category"
            value={selectedTourExtraCategory}
            label={<Translate contentKey="jhipsterSampleApplicationApp.content.tourExtraCategory">Tour Extra Category</Translate>}
            onChange={handleTourExtraCategoryFilterChange}
          >
            <MenuItem value="">
              <em>All Tour Extra Categories</em>
            </MenuItem>
            {tourExtraCategories
              ? tourExtraCategories.map(tec => (
                  <MenuItem value={tec.id} key={tec.id}>
                    {tec.code}
                  </MenuItem>
                ))
              : null}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="content-filter-tour-extra-label">
            <Translate contentKey="jhipsterSampleApplicationApp.content.tourExtra">Tour Extra</Translate>
          </InputLabel>
          <Select
            labelId="content-filter-tour-extra-label"
            id="content-filter-tour-extra"
            value={selectedTourExtra}
            label={<Translate contentKey="jhipsterSampleApplicationApp.content.tourExtra">Tour Extra</Translate>}
            onChange={handleTourExtraFilterChange}
          >
            <MenuItem value="">
              <em>All Tour Extras</em>
            </MenuItem>
            {tourExtras
              ? tourExtras.map(te => (
                  <MenuItem value={te.id} key={te.id}>
                    {te.code}
                  </MenuItem>
                ))
              : null}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="content-filter-menu-label">
            <Translate contentKey="jhipsterSampleApplicationApp.content.menu">Menu</Translate>
          </InputLabel>
          <Select
            labelId="content-filter-menu-label"
            id="content-filter-menu"
            value={selectedMenu}
            label={<Translate contentKey="jhipsterSampleApplicationApp.content.menu">Menu</Translate>}
            onChange={handleMenuFilterChange}
          >
            <MenuItem value="">
              <em>All Menus</em>
            </MenuItem>
            {menus
              ? menus.map(m => (
                  <MenuItem value={m.id} key={m.id}>
                    {m.code}
                  </MenuItem>
                ))
              : null}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="content-filter-web-page-label">
            <Translate contentKey="jhipsterSampleApplicationApp.content.webPage">Web Page</Translate>
          </InputLabel>
          <Select
            labelId="content-filter-web-page-label"
            id="content-filter-web-page"
            value={selectedWebPage}
            label={<Translate contentKey="jhipsterSampleApplicationApp.content.webPage">Web Page</Translate>}
            onChange={handleWebPageFilterChange}
          >
            <MenuItem value="">
              <em>All Web Pages</em>
            </MenuItem>
            {webPages
              ? webPages.map(wp => (
                  <MenuItem value={wp.id} key={wp.id}>
                    {wp.code}
                  </MenuItem>
                ))
              : null}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="content-filter-tag-label">
            <Translate contentKey="jhipsterSampleApplicationApp.content.tag">Tag</Translate>
          </InputLabel>
          <Select
            labelId="content-filter-tag-label"
            id="content-filter-tag"
            value={selectedTag}
            label={<Translate contentKey="jhipsterSampleApplicationApp.content.tag">Tag</Translate>}
            onChange={handleTagFilterChange}
          >
            <MenuItem value="">
              <em>All Tags</em>
            </MenuItem>
            {tags
              ? tags.map(t => (
                  <MenuItem value={t.id} key={t.id}>
                    {t.code}
                  </MenuItem>
                ))
              : null}
          </Select>
        </FormControl>
        {hasActiveFilters && (
          <Button variant="text" color="error" size="small" onClick={handleClearAllFilters}>
            Clear Filters
          </Button>
        )}
      </Box>
      <div className="table-responsive">
        {contentList && contentList.length > 0 ? (
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <MuiTable>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('code')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.content.code">Code</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('code')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('title')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.content.title">Title</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('title')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('shortDescription')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.content.shortDescription">Short Description</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('shortDescription')} />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.content.language">Language</Translate>{' '}
                    <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.content.destination">Destination</Translate>{' '}
                    <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.content.tourCategory">Tour Category</Translate>{' '}
                    <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.content.place">Place</Translate> <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.content.placeCategory">Place Category</Translate>{' '}
                    <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.content.tourExtraCategory">Tour Extra Category</Translate>{' '}
                    <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.content.tourExtra">Tour Extra</Translate>{' '}
                    <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.content.menu">Menu</Translate> <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.content.webPage">Web Page</Translate>{' '}
                    <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.content.tag">Tag</Translate> <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.content.tourStep">Tour Step</Translate>{' '}
                    <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.content.promotion">Promotion</Translate>{' '}
                    <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.content.imageFile">Image File</Translate>{' '}
                    <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {contentList.map((content, i) => (
                  <TableRow key={`entity-${i}`} data-cy="entityTable">
                    <TableCell>
                      <Button component={Link} to={`/content/${content.id}`} variant="text" size="small">
                        {content.code}
                      </Button>
                    </TableCell>
                    <TableCell>{content.title}</TableCell>
                    <TableCell>{content.shortDescription}</TableCell>
                    <TableCell>
                      {content.language ? <Link to={`/language/${content.language.id}`}>{content.language.code}</Link> : ''}
                    </TableCell>
                    <TableCell>
                      {content.destination ? <Link to={`/destination/${content.destination.id}`}>{content.destination.code}</Link> : ''}
                    </TableCell>
                    <TableCell>
                      {content.tourCategory ? (
                        <Link to={`/tour-category/${content.tourCategory.id}`}>{content.tourCategory.code}</Link>
                      ) : (
                        ''
                      )}
                    </TableCell>
                    <TableCell>{content.place ? <Link to={`/place/${content.place.id}`}>{content.place.code}</Link> : ''}</TableCell>
                    <TableCell>
                      {content.placeCategory ? (
                        <Link to={`/place-category/${content.placeCategory.id}`}>{content.placeCategory.code}</Link>
                      ) : (
                        ''
                      )}
                    </TableCell>
                    <TableCell>
                      {content.tourExtraCategory ? (
                        <Link to={`/tour-extra-category/${content.tourExtraCategory.id}`}>{content.tourExtraCategory.code}</Link>
                      ) : (
                        ''
                      )}
                    </TableCell>
                    <TableCell>
                      {content.tourExtra ? <Link to={`/tour-extra/${content.tourExtra.id}`}>{content.tourExtra.code}</Link> : ''}
                    </TableCell>
                    <TableCell>{content.menu ? <Link to={`/menu/${content.menu.id}`}>{content.menu.code}</Link> : ''}</TableCell>
                    <TableCell>
                      {content.webPage ? <Link to={`/web-page/${content.webPage.id}`}>{content.webPage.code}</Link> : ''}
                    </TableCell>
                    <TableCell>{content.tag ? <Link to={`/tag/${content.tag.id}`}>{content.tag.code}</Link> : ''}</TableCell>
                    <TableCell>
                      {content.tourStep ? <Link to={`/tour-step/${content.tourStep.id}`}>{content.tourStep.code}</Link> : ''}
                    </TableCell>
                    <TableCell>
                      {content.promotion ? <Link to={`/promotion/${content.promotion.id}`}>{content.promotion.code}</Link> : ''}
                    </TableCell>
                    <TableCell>
                      {content.imageFile ? <Link to={`/image-file/${content.imageFile.id}`}>{content.imageFile.code}</Link> : ''}
                    </TableCell>
                    <TableCell className="text-end">
                      <ButtonGroup variant="outlined" size="small" aria-label="entity actions">
                        <Button
                          component={Link}
                          to={`/content/${content.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                          color="primary"
                          startIcon={<FontAwesomeIcon icon="pencil-alt" />}
                        >
                        </Button>
                        <Button
                          onClick={() =>
                            (window.location.href = `/content/${content.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                          }
                          color="error"
                          startIcon={<FontAwesomeIcon icon="trash" />}
                        >
                        </Button>
                      </ButtonGroup>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </MuiTable>
          </TableContainer>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="jhipsterSampleApplicationApp.content.home.notFound">No Contents found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={contentList && contentList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default Content;
